import { create } from "zustand";
import * as Crypto from "expo-crypto";
import type { AlarmContacts } from "@visioni/a3s-alarm-schedules/schema";

export type UnifiedContact = {
  id: string;
  name: string;
  email?: string;
  phoneNumber?: string;
};

type ContactsState = {
  /** Unified contacts keyed by `siteId:cameraKey` */
  contactsByCamera: Record<string, UnifiedContact[]>;
  /** Merge API-shaped contacts into the unified model for a given camera. */
  syncFromApi: (siteId: string, cameraKey: string, apiContacts: AlarmContacts) => void;
  /** Get unified contacts for a camera. */
  getContacts: (siteId: string, cameraKey: string) => UnifiedContact[];
};

/** Deterministic key for a camera's contact store entry. */
function cameraStoreKey(siteId: string, camKey: string) {
  return `${siteId}:${camKey}`;
}

function generateId() {
  return Crypto.randomUUID();
}

/**
 * Merge the API's separate email_contacts / sms_contacts arrays into a single
 * unified list. Groups by shared `id` field first, then falls back to name matching.
 */
export function mergeApiContacts(apiContacts: AlarmContacts): UnifiedContact[] {
  const byId = new Map<string, UnifiedContact>();
  const byName = new Map<string, UnifiedContact>();

  const findOrCreate = (id: string | undefined, name: string): UnifiedContact => {
    // Try matching by API id first
    if (id) {
      const existing = byId.get(id);
      if (existing) return existing;
    }
    // Fall back to name matching
    const nameKey = name.toLowerCase();
    const existing = byName.get(nameKey);
    if (existing) return existing;

    // Create new unified contact
    const contact: UnifiedContact = { id: id ?? generateId(), name };
    if (id) byId.set(id, contact);
    byName.set(nameKey, contact);
    return contact;
  };

  for (const ec of apiContacts.email_contacts) {
    const name = ec.name || ec.email;
    const contact = findOrCreate(ec.id, name);
    contact.email = contact.email ?? ec.email;
    // Prefer a real name over email-as-name
    if (ec.name && contact.name === contact.email) {
      contact.name = ec.name;
    }
  }

  for (const sc of apiContacts.sms_contacts) {
    const contact = findOrCreate(sc.id, sc.name);
    contact.phoneNumber = contact.phoneNumber ?? sc.phone_number;
    // SMS always has a real name — prefer it
    if (contact.name === contact.email) {
      contact.name = sc.name;
    }
  }

  return Array.from(new Set([...byId.values(), ...byName.values()]));
}

/**
 * Convert unified contacts back into the API's separate arrays format.
 * Writes the same `id` on both email and SMS entries so they can be re-merged.
 */
export function splitToApiContacts(contacts: UnifiedContact[]): AlarmContacts {
  const email_contacts: AlarmContacts["email_contacts"] = [];
  const sms_contacts: AlarmContacts["sms_contacts"] = [];

  for (const c of contacts) {
    if (c.email) {
      email_contacts.push({ id: c.id, email: c.email, name: c.name });
    }
    if (c.phoneNumber) {
      sms_contacts.push({ id: c.id, name: c.name, phone_number: c.phoneNumber });
    }
  }

  return { email_contacts, sms_contacts };
}

export const useContactsStore = create<ContactsState>((set, get) => ({
  contactsByCamera: {},

  syncFromApi: (siteId, camKey, apiContacts) => {
    const key = cameraStoreKey(siteId, camKey);
    const unified = mergeApiContacts(apiContacts);
    set((state) => ({
      contactsByCamera: { ...state.contactsByCamera, [key]: unified },
    }));
  },

  getContacts: (siteId, camKey) => {
    const key = cameraStoreKey(siteId, camKey);
    return get().contactsByCamera[key] ?? [];
  },
}));
